/* eslint-disable ember/no-runloop */
import { getOwner } from "@ember/-internals/owner";
import type { Timer } from "@ember/runloop";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import type Store from "ember-data/store";
import Base from "ember-simple-auth/authenticators/base";
import type ApplicationAdapter from "parog-web/adapters/application";
import type NativeAppService from "parog-web/services/native-app";
import type NetworkDetectorService from "parog-web/services/network-detector";
import type SemeiaSessionService from "parog-web/services/session";
import ControlTower from "wise-control-tower";

export default class PatientImplicitAuthenticator extends Base {
  @service declare nativeApp: NativeAppService;
  @service declare networkDetector: NetworkDetectorService;
  @service requestManager;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  declare _refreshTokenTimeout?: Timer;
  applicationAdapter = getOwner(this)?.lookup(
    "adapter:application"
  ) as ApplicationAdapter;
  tokenEndpoint = "/api/v1/get_tokens";

  override async authenticate(requestData) {
    // If the patient is already authenticated, keep him logged in
    // with the previous access token if the new one is already expired
    if (
      this.session.isAuthenticated &&
      this.session.data.authenticated.as === "patient"
    ) {
      this.nativeApp.patientLoggedIn();
      const connectionInfo = this.networkDetector.detectConnection();

      await this.saveConnectionInfo(
        connectionInfo,
        "Patient",
        this.session.patient?.id,
        "NativeMobileApp"
      );
      return this.session.data.authenticated;
    }

    if (this.session.refreshToken) {
      this.nativeApp.patientLoggedIn();
      return this.session.data.authenticated;
    }

    let response;
    try {
      const { content } = await this.requestManager.request({
        url: this.tokenEndpoint,
        method: "POST",
        body: JSON.stringify(requestData)
      });
      response = content;
    } catch ({ response, content }) {
      if (!response.ok) throw content;
    }

    const data = {
      as: "patient",
      id: response.id,
      uuid: response.id,
      health_centre_id: response.health_centre_id,
      health_centre_uuid: response.health_centre_uuid,
      access_token: response.access_token,
      refresh_token: response.refresh_token
    };

    await ControlTower.setUser({ ...data, class: "Patient" });
    this.nativeApp.patientLoggedIn();

    this.session.isAuthenticatedAtThisSession = true;
    await this.session.fetchAuthenticatedUser(data);
    return data;
  }

  override async invalidate() {
    await this.session.revokeToken();
    cancel(this._refreshTokenTimeout);
    delete this._refreshTokenTimeout;
    await ControlTower.resetUser();
  }

  override async restore(data) {
    if (data.refresh_token) {
      // Do not authenticate if refresh token is expired
      if (!this.session.isTokenValid(data.refresh_token)) {
        await ControlTower.resetUser();
        throw "Patient Implicit Authenticator - Invalid refresh token";
      }

      // If access token is expired, refresh it and the RT
      if (!this.session.isTokenValid(data.access_token)) {
        await this.session.refreshTokens();
      }

      // Otherwise, do nothing and schedule next tokens refresh
      this.session.scheduleNextRefresh(data.access_token);
    }

    await ControlTower.setUser({ ...data, class: "Patient" });
    await this.session.fetchAuthenticatedUser(data);
    return data;
  }

  async saveConnectionInfo(connectionInfo, creatorType, creatorId, appType) {
    if (!connectionInfo) return;

    await this.store
      .createRecord("user-connection-journal-event", {
        occurredAt: new Date(),
        networkInformation: connectionInfo.effectiveType,
        creatorType: creatorType,
        creatorId: creatorId,
        appType: appType
      })
      .save();
  }
}
